import ISignupUser, { ACCOUNT_TYPES, parseAccountType } from '@/app/(auth)/signup/types/ISignupUser';
import { z } from 'zod';

export interface IValidationResult {
  isPresent: boolean;
  isValid: boolean;
  errors: { [field: string]: string[] };
  fieldValidity: { [field: string]: boolean };
}

export interface IFieldValidationResult {
  fieldName: keyof ISignupUser;
  isPresent: boolean;
  isValid: boolean;
  errors: string[];
}

// This partially duplicates what we have in SignupValidator, but where that does deep validation, this is intended to
// get fields into the right types so we can carry out the deep validation next
export const signupStateSchema = z.object({
  accountType: z.preprocess(
    value => (typeof value === 'string' ? (parseAccountType(value) ?? value) : value),
    z.enum(ACCOUNT_TYPES).optional(),
  ),
  homeLocation: z.coerce.number().optional(),
  name: z.string().optional(),
  salutation: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  businessName: z.string().optional(),
  companyNumber: z.string().optional(),
  tradingName: z.string().optional(),
  address: z.string().optional(),
  address2: z.string().optional(),
  county: z.string().optional(),
  postCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  vatNumber: z.string().optional(),
  terms: z.coerce.boolean().optional(),
  hasMarketingConsent: z.coerce.boolean().optional(),
  isFixingUp: z.coerce.boolean().optional(),
});

abstract class FieldValidation {
  constructor(protected readonly value: ISignupUser) {}

  abstract fieldName: keyof ISignupUser;

  abstract validate(): IFieldValidationResult;
}

class ExistsValidation extends FieldValidation {
  constructor(
    protected readonly value: ISignupUser,
    public readonly fieldName: keyof ISignupUser,
  ) {
    super(value);
  }

  validate(): IFieldValidationResult {
    const value = this.value[this.fieldName];

    if (typeof value === 'string' && value.trim() === '') {
      // If the value is a string and it's empty don't allow it
      return {
        fieldName: this.fieldName,
        isPresent: false,
        isValid: false,
        errors: [],
      };
    }
    return {
      fieldName: this.fieldName,
      isPresent: !!this.value[this.fieldName],
      isValid: !!this.value[this.fieldName],
      errors: [],
    };
  }
}

class NoValidation extends FieldValidation {
  constructor(
    protected readonly value: ISignupUser,
    public readonly fieldName: keyof ISignupUser,
  ) {
    super(value);
  }

  validate(): IFieldValidationResult {
    return {
      fieldName: this.fieldName,
      isPresent: true,
      isValid: true,
      errors: [],
    };
  }
}

class TermsValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'terms';

  validate(): IFieldValidationResult {
    return {
      fieldName: this.fieldName,
      isPresent: true,
      isValid: this.value.terms === true,
      errors: [],
    };
  }
}
class EmailValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'email';

  validate(): IFieldValidationResult {
    const isPresent = !!this.value.email;
    let isValid = isPresent && !!this.value.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

    let errorMessage = '';
    if (isPresent && isValid) {
      if (this.value.email?.endsWith('@example.com')) {
        isValid = false;
        errorMessage = 'Email addresses ending with "@example.com" are not allowed.';
      }
    } else {
      errorMessage = 'Please enter a valid email address.';
    }

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: errorMessage ? [errorMessage] : [],
    };
  }
}

class NameValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'name';

  validate(): IFieldValidationResult {
    const name = this.value.name || '';
    const isPresent = !!this.value.name;
    const isValidFormat = /^[a-zA-ZÀ-ž'-]+ [a-zA-ZÀ-ž][a-zA-ZÀ-ž '-]*$/.test(name);
    const isLongEnough = name.length > 3;
    const isValid = isPresent && isValidFormat && isLongEnough;

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: (!isValid && isPresent && ['Please enter your full name. e.g. "John Smith"']) || [],
    };
  }
}

class BusinessNameValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'businessName';

  validate(): IFieldValidationResult {
    // Business name is only required for business accounts
    if (this.value.accountType !== 'business') {
      return {
        fieldName: this.fieldName,
        isPresent: true,
        isValid: true,
        errors: [],
      };
    }

    return new ExistsValidation(this.value, this.fieldName).validate();
  }
}

class AccountTypeValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'accountType';

  validate(): IFieldValidationResult {
    const isPresent = !!this.value.accountType;
    const isValid = isPresent && !!this.value.accountType?.match(/^(business|individual)$/);

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: (!isValid && isPresent && ['Please select an account type']) || [],
    };
  }
}

class PhoneValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'phone';

  validate(): IFieldValidationResult {
    const isPresent = !!this.value.phone;
    const isValid =
      isPresent && !!this.value.phone?.match(/^((\+[1-9]{1,3}(\s\(0\)\s|\s0\s|\s)?)|0)(\s)?(\d(\s)?){9,11}$/);

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: (!isValid && isPresent && ['Please enter a valid phone number. e.g. "+44 123 456 7890"']) || [],
    };
  }
}

class PostCodeValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'postCode';

  validate(): IFieldValidationResult {
    const isPresent = !!this.value.postCode;
    // UK postcode regex
    const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
    // Irish Eircode regex
    const irishEircodeRegex = /^[A-Z]\d{2}\s[A-Z0-9]{4}$/i;

    const isValid =
      isPresent &&
      (!!this.value.postCode?.match(ukPostcodeRegex) || !!this.value.postCode?.match(irishEircodeRegex));

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: (!isValid && isPresent && ['Please enter a valid UK postcode or Irish Eircode']) || [],
    };
  }
}

class VatNumberValidation extends FieldValidation {
  fieldName: keyof ISignupUser = 'vatNumber';

  validate(): IFieldValidationResult {
    const isPresent = !!this.value.vatNumber;
    const isValid =
      !isPresent ||
      !!this.value.vatNumber?.match(
        /^(GB|gb)?\s*[0-9]{9,12}$|^(IE|ie)?\s*([0-9]{7}[A-Za-z][A-Za-z]?|[A-Za-z][0-9]{6}[A-Za-z]|[0-9][A-Za-z][0-9]{5}[A-Za-z])$|^[0-9]{7}[A-Za-z]$|^[0-9]{6}[A-Za-z]{2}$/,
      );

    return {
      fieldName: this.fieldName,
      isPresent,
      isValid,
      errors: !isValid ? ['Please enter a valid VAT number'] : [],
    };
  }
}

export class SignupValidator {
  validators: Record<keyof ISignupUser, FieldValidation>;

  constructor(protected readonly value: ISignupUser) {
    this.validators = {
      name: new NameValidation(value),
      accountType: new AccountTypeValidation(value),
      homeLocation: new ExistsValidation(value, 'homeLocation'),
      salutation: new NoValidation(value, 'salutation'),
      phone: new PhoneValidation(value),
      email: new EmailValidation(value),
      businessName: new BusinessNameValidation(value),
      companyNumber: new NoValidation(value, 'companyNumber'),
      tradingName: new NoValidation(value, 'tradingName'),
      address: new ExistsValidation(value, 'address'),
      address2: new ExistsValidation(value, 'address2'),
      postCode: new PostCodeValidation(value),
      state: new ExistsValidation(value, 'state'),
      city: new ExistsValidation(value, 'city'),
      vatNumber: new VatNumberValidation(value),
      terms: new TermsValidation(value),
      hasMarketingConsent: new NoValidation(value, 'hasMarketingConsent'),
      oktaId: new NoValidation(value, 'oktaId'),
      isFixingUp: new NoValidation(value, 'isFixingUp'),
      hasStartedVerification: new NoValidation(value, 'hasStartedVerification'),
      activationNeedsPersonalDetails: new NoValidation(value, 'activationNeedsPersonalDetails'),
    };
  }

  validate(onlyFields: (keyof ISignupUser)[]): IValidationResult {
    if (onlyFields.length === 0) {
      return {
        isPresent: true,
        isValid: true,
        errors: {},
        fieldValidity: {},
      };
    }
    const fieldsToCheck = (
      onlyFields.length === 0 ? Object.keys(this.validators) : onlyFields
    ) as (keyof ISignupUser)[];

    const results = fieldsToCheck.map(field => this.validators[field].validate());
    const isValid = results.every(result => result.isValid);
    const isPresent = results.every(result => result.isPresent);
    const errors = results.reduce(
      (acc, result) => {
        if (result.errors) {
          // Combine the new errors with any existing ones for this field
          acc[result.fieldName] = [...(acc[result.fieldName] || []), ...result.errors];
        }
        return acc;
      },
      {} as { [field: string]: string[] },
    );

    const fieldValidity = results.reduce(
      (acc, result) => {
        acc[result.fieldName] = result.isValid;
        return acc;
      },
      {} as { [field: string]: boolean },
    );

    return {
      isPresent,
      isValid,
      errors,
      fieldValidity,
    };
  }
}
